import axios from 'axios';

const AGENT_BASE_URL = 'http://localhost:8001';

export interface ChatContext {
  channel_id?: string;
  show_id?: string;
  episode_id?: string;
  segment_id?: string;
}

export interface ChatRequest {
  prompt: string;
  channel_id?: string;
  show_id?: string;
  episode_id?: string;
  segment_id?: string;
}

export interface ChatResponse {
  success: boolean;
  response: string;
  actions_taken?: any[];
  data?: any;
  error?: string;
}

export interface ChatMessage {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  actions?: any[];
  data?: any;
}

class ChatService {
  private isAvailable: boolean | null = null;
  private lastCheck: number = 0;
  private readonly CHECK_INTERVAL = 30000; // 30 seconds

  /**
   * Check if the AI agent is available
   */
  async checkAvailability(): Promise<boolean> {
    const now = Date.now();
    
    // Return cached result if checked recently
    if (this.isAvailable !== null && (now - this.lastCheck) < this.CHECK_INTERVAL) {
      return this.isAvailable;
    }

    try {
      // Try to ping the agent health endpoint or make a simple request
      await axios.get(`${AGENT_BASE_URL}/health`, { timeout: 5000 });
      this.isAvailable = true;
    } catch (error) {
      // If no health endpoint, try the chat endpoint with a simple request
      try {
        await axios.post(`${AGENT_BASE_URL}/api/v1/chat`, {
          prompt: 'test'
        }, { timeout: 5000 });
        this.isAvailable = true;
      } catch (chatError) {
        this.isAvailable = false;
      }
    }

    this.lastCheck = now;
    return this.isAvailable;
  }

  /**
   * Send a message to the AI agent
   */
  async sendMessage(request: ChatRequest): Promise<ChatResponse> {
    try {
      const response = await axios.post<ChatResponse>(
        `${AGENT_BASE_URL}/api/v1/chat`,
        request,
        {
          timeout: 30000, // 30 second timeout
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Chat service error:', error);
      
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
          throw new Error('AI agent is not available. Please try again later.');
        }
        
        if (error.response?.status === 500) {
          throw new Error('AI agent encountered an error. Please try again.');
        }
      }
      
      throw new Error('Failed to send message. Please try again.');
    }
  }

  /**
   * Extract context from current route
   */
  extractContextFromRoute(pathname: string, params: Record<string, string | undefined>): ChatContext {
    const context: ChatContext = {};

    // Extract context from URL params
    if (params.channelId) {
      context.channel_id = params.channelId;
    }
    if (params.showId) {
      context.show_id = params.showId;
    }
    if (params.episodeId) {
      context.episode_id = params.episodeId;
    }
    if (params.segmentId) {
      context.segment_id = params.segmentId;
    }

    return context;
  }

  /**
   * Get context description for display
   */
  getContextDescription(context: ChatContext): string {
    const parts: string[] = [];
    
    if (context.channel_id) parts.push('Channel');
    if (context.show_id) parts.push('Show');
    if (context.episode_id) parts.push('Episode');
    if (context.segment_id) parts.push('Segment');
    
    return parts.length > 0 ? `Working with: ${parts.join(' → ')}` : 'No specific context';
  }

  /**
   * Generate context-aware suggestions based on current page
   */
  getContextualSuggestions(context: ChatContext): string[] {
    const suggestions: string[] = [];

    if (context.segment_id) {
      suggestions.push(
        'Add news articles to this segment',
        'Generate a script for this segment',
        'Search for relevant news topics'
      );
    } else if (context.episode_id) {
      suggestions.push(
        'Add a new segment to this episode',
        'Get all segments in this episode',
        'Publish this episode'
      );
    } else if (context.show_id) {
      suggestions.push(
        'Create a new episode for this show',
        'Get all episodes in this show',
        'Update show settings'
      );
    } else if (context.channel_id) {
      suggestions.push(
        'Create a new show in this channel',
        'Get all shows in this channel',
        'View channel analytics'
      );
    } else {
      suggestions.push(
        'Create a new channel',
        'View all channels',
        'Search for news articles'
      );
    }

    return suggestions;
  }
}

export const chatService = new ChatService();
export default chatService;
